import React, { useState, useEffect, useRef, useCallback } from 'react';
import { uploadBrainDocument, getBrainDocuments, deleteBrainDocument, reprocessBrainDocument } from '../../api/documents';
import { useToast } from '../../hooks/useToast';

const ALLOWED_EXTENSIONS = ['.pdf', '.docx', '.doc', '.xlsx', '.xls', '.csv', '.txt', '.md'];
const MAX_SIZE_BYTES = 10 * 1024 * 1024;

const STATUS_BADGES = {
  pending: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Pending' },
  extracting: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Extracting Text' },
  extracted: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Extracted' },
  processing: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Analyzing' },
  completed: { bg: 'bg-green-100', text: 'text-green-700', label: 'Processed' },
  failed: { bg: 'bg-red-100', text: 'text-red-700', label: 'Failed' },
};

const BrainDocumentsTab = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const toast = useToast();
  const pollRef = useRef(null);

  const fetchDocuments = useCallback(async () => {
    try {
      const res = await getBrainDocuments();
      setDocuments(res.data?.documents || []);
    } catch {
      // Silent
    }
  }, []);

  useEffect(() => {
    fetchDocuments().finally(() => setLoading(false));
  }, [fetchDocuments]);

  // Poll while processing
  useEffect(() => {
    const hasProcessing = documents.some(d =>
      ['pending', 'extracting', 'extracted', 'processing'].includes(d.processing_status)
    );
    if (hasProcessing) {
      pollRef.current = setInterval(fetchDocuments, 5000);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [documents, fetchDocuments]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      toast.error(`Unsupported file type. Allowed: ${ALLOWED_EXTENSIONS.join(', ')}`);
      return;
    }
    if (file.size > MAX_SIZE_BYTES) {
      toast.error('File size exceeds 10MB limit');
      return;
    }

    setUploading(true);
    try {
      await uploadBrainDocument(file);
      toast.success('Document uploaded. Processing started.');
      await fetchDocuments();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Upload failed';
      toast.error(msg);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await deleteBrainDocument(docId);
      toast.success('Document deleted');
      await fetchDocuments();
    } catch {
      toast.error('Failed to delete document');
    }
  };

  const handleReprocess = async (docId) => {
    try {
      await reprocessBrainDocument(docId);
      toast.success('Reprocessing started');
      await fetchDocuments();
    } catch {
      toast.error('Failed to start reprocessing');
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Documents</h2>
          <p className="text-xs text-gray-500">
            Upload documents to automatically extract Structured Entities and Internal Knowledge updates.
          </p>
        </div>
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition"
        >
          {uploading ? 'Uploading...' : 'Upload Document'}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept={ALLOWED_EXTENSIONS.join(',')}
          onChange={handleUpload}
          className="hidden"
        />
      </div>

      {/* Content */}
      {loading ? (
        <div className="text-center py-12 text-sm text-gray-500">Loading documents...</div>
      ) : documents.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-xl">
          <svg className="w-10 h-10 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p className="text-sm text-gray-500 mb-1">No documents uploaded yet</p>
          <p className="text-xs text-gray-400">
            Upload PDF, DOCX, XLSX, CSV, or TXT files (max 10MB) to auto-extract knowledge.
          </p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-2.5 text-left font-medium text-gray-600">Filename</th>
                <th className="px-4 py-2.5 text-left font-medium text-gray-600">Size</th>
                <th className="px-4 py-2.5 text-left font-medium text-gray-600">Uploaded</th>
                <th className="px-4 py-2.5 text-left font-medium text-gray-600">Status</th>
                <th className="px-4 py-2.5 text-left font-medium text-gray-600">Result</th>
                <th className="px-4 py-2.5 text-right font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {documents.map((doc) => {
                const badge = STATUS_BADGES[doc.processing_status] || STATUS_BADGES.pending;
                return (
                  <tr key={doc.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <span className="font-medium text-gray-900">{doc.filename}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{formatSize(doc.size_bytes)}</td>
                    <td className="px-4 py-3 text-gray-600">{formatDate(doc.created_at)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${badge.bg} ${badge.text}`}>
                        {badge.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {doc.processing_status === 'completed' && doc.processing_result?.drafts_created != null ? (
                        <span className="text-green-600 font-medium">
                          {doc.processing_result.drafts_created} draft{doc.processing_result.drafts_created !== 1 ? 's' : ''} created
                        </span>
                      ) : doc.processing_status === 'failed' ? (
                        <span className="text-red-600 truncate max-w-[150px] inline-block" title={doc.processing_error}>
                          {doc.processing_error || 'Processing failed'}
                        </span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {doc.download_url && (
                          <a href={doc.download_url} target="_blank" rel="noopener noreferrer"
                             className="text-gray-400 hover:text-blue-600 transition" title="Download">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                          </a>
                        )}
                        {(doc.processing_status === 'failed' || doc.processing_status === 'completed') && (
                          <button onClick={() => handleReprocess(doc.id)}
                                  className="text-gray-400 hover:text-blue-600 transition" title="Reprocess">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                          </button>
                        )}
                        <button onClick={() => handleDelete(doc.id)}
                                className="text-gray-400 hover:text-red-600 transition" title="Delete">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default BrainDocumentsTab;
